/**
 * Colour math for the token generator: sRGB <-> OKLab/OKLCH (Bjorn Ottosson's
 * reference matrices), sRGB gamut mapping by chroma reduction, WCAG 2.x
 * relative luminance and contrast ratio, and OKLab distance.
 *
 * Everything here is pure and deterministic so the generated theme.css is
 * byte-stable across machines.
 */

export interface Rgb {
  /** 0..1 gamma-encoded sRGB channels. */
  r: number;
  g: number;
  b: number;
}

export interface Oklch {
  /** Perceptual lightness, 0..1. */
  l: number;
  /** Chroma, 0..~0.37. */
  c: number;
  /** Hue angle in degrees, 0..360. */
  h: number;
}

interface Oklab {
  l: number;
  a: number;
  b: number;
}

const HEX_RE = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

export function parseHex(hex: string): Rgb {
  const m = HEX_RE.exec(hex.trim());
  if (!m || m[1] === undefined) throw new Error(`Not a hex colour: ${hex}`);
  let digits = m[1];
  if (digits.length === 3) {
    digits = [...digits].map((d) => d + d).join('');
  }
  const n = Number.parseInt(digits, 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

function toByte(v: number): number {
  return Math.round(Math.min(1, Math.max(0, v)) * 255);
}

/** Formats as lowercase #rrggbb, or #rrggbbaa when alpha < 1. */
export function formatHex(rgb: Rgb, alpha = 1): string {
  const hex = [rgb.r, rgb.g, rgb.b].map((v) => toByte(v).toString(16).padStart(2, '0')).join('');
  if (alpha >= 1) return `#${hex}`;
  return `#${hex}${toByte(alpha).toString(16).padStart(2, '0')}`;
}

function srgbToLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(v: number): number {
  return v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
}

function rgbToOklab({ r, g, b }: Rgb): Oklab {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

/** OKLab to linear-light sRGB (unclamped, may fall outside 0..1). */
function oklabToLinear({ l, a, b }: Oklab): Rgb {
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return {
    r: 4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    g: -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    b: -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  };
}

function lchToLab({ l, c, h }: Oklch): Oklab {
  const rad = (h * Math.PI) / 180;
  return { l, a: c * Math.cos(rad), b: c * Math.sin(rad) };
}

export function rgbToOklch(rgb: Rgb): Oklch {
  const { l, a, b } = rgbToOklab(rgb);
  const c = Math.hypot(a, b);
  let h = (Math.atan2(b, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l, c, h: c < 1e-6 ? 0 : h };
}

export function hexToOklch(hex: string): Oklch {
  return rgbToOklch(parseHex(hex));
}

const GAMUT_EPS = 1e-6;

function inGamut(lin: Rgb): boolean {
  return [lin.r, lin.g, lin.b].every((v) => v >= -GAMUT_EPS && v <= 1 + GAMUT_EPS);
}

/**
 * Converts OKLCH to sRGB, reducing chroma at constant lightness and hue until
 * the colour fits the sRGB gamut (binary search, CSS Color 4 style). Lightness
 * and hue are what the ramps promise, so chroma is the variable we give up.
 */
export function oklchToRgb(color: Oklch): Rgb {
  const l = Math.min(1, Math.max(0, color.l));
  let lin = oklabToLinear(lchToLab({ ...color, l }));
  if (!inGamut(lin)) {
    let lo = 0;
    let hi = color.c;
    for (let i = 0; i < 32; i += 1) {
      const mid = (lo + hi) / 2;
      if (inGamut(oklabToLinear(lchToLab({ l, c: mid, h: color.h })))) lo = mid;
      else hi = mid;
    }
    lin = oklabToLinear(lchToLab({ l, c: lo, h: color.h }));
  }
  return {
    r: linearToSrgb(clamp01(lin.r)),
    g: linearToSrgb(clamp01(lin.g)),
    b: linearToSrgb(clamp01(lin.b)),
  };
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

export function oklchToHex(color: Oklch, alpha = 1): string {
  return formatHex(oklchToRgb(color), alpha);
}

/** WCAG 2.x relative luminance of a hex colour. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** WCAG 2.x contrast ratio, 1..21. Order of arguments does not matter. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Euclidean distance in OKLab (a "deltaE OK"); 0.02 is roughly a just-visible step. */
export function deltaEOk(a: string, b: string): number {
  const pa = rgbToOklab(parseHex(a));
  const pb = rgbToOklab(parseHex(b));
  return Math.hypot(pa.l - pb.l, pa.a - pb.a, pa.b - pb.b);
}

/**
 * Composites a translucent colour over an opaque one (both hex), returning the
 * opaque result. Used to check contrast of glass chrome and soft fills.
 */
export function composite(top: string, alpha: number, bottom: string): string {
  const t = parseHex(top);
  const b = parseHex(bottom);
  return formatHex({
    r: t.r * alpha + b.r * (1 - alpha),
    g: t.g * alpha + b.g * (1 - alpha),
    b: t.b * alpha + b.b * (1 - alpha),
  });
}
